'use client';

import { HeartIcon } from '@heroicons/react/24/outline';
import { HeartIcon as HeartIconSolid } from '@heroicons/react/24/solid';

import { useHeartInteractions } from '@/lib/client/useHeartInteractions';
import { trackWishlistSavedClick } from '@/lib/client/gtm';
/**
 * Button component for saving/unsaving hearts
 * Shows saved state and handles optimistic UI updates
 * Works for both authenticated and guest (non-authenticated) users
 * @param {number} heartId - The heart ID to save/unsave
 * @param {string} heartName - The heart name for GTM tracking
 * @param {string} className - Additional CSS classes
 * @param {boolean} iconOnly - If true, shows only icon without text
 */
export default function HeartSaveButton({
  heartId,
  heartName,
  className = '',
  iconOnly = false,
  hideText = false,
  onSave,
}) {
  const { saved, toggleSave, loading, saving } = useHeartInteractions(heartId);

  const handleClick = () => {
    // Track when user clicks to save (not unsave)
    if (!saved) {
      trackWishlistSavedClick({ heart_name: heartName });
    }
    toggleSave();
    onSave?.(!saved);
  };

  // Show loading state while fetching initial state
  if (loading) {
    if (iconOnly) {
      return (
        <button
          disabled
          className={`inline-flex items-center justify-center ${className}`}
          aria-label="Loading save status"
        >
          <HeartIcon className="w-5 h-5" />
          {!hideText && <span className="hidden lg:block">Save</span>}
        </button>
      );
    }
    return (
      <button
        disabled
        className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-gray-200 text-gray-500 cursor-not-allowed ${className}`}
        aria-label="Loading save status"
      >
        <HeartIcon className="w-5 h-5" />
        <span>Loading...</span>
      </button>
    );
  }

  if (iconOnly) {
    return (
      <button
        onClick={handleClick}
        disabled={saving}
        className={`inline-flex items-center justify-center transition-opacity ${
          saving ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'
        } ${className}`}
        aria-label={saved ? 'Unsave heart' : 'Save heart'}
      >
        {saved ? <HeartIconSolid className="w-5 h-5" /> : <HeartIcon className="w-5 h-5" />}

        {!hideText && <span className="hidden lg:block">{saved ? 'Saved' : 'Save'}</span>}
      </button>
    );
  }

  return (
    <button
      onClick={handleClick}
      disabled={saving}
      className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg transition-colors ${
        saved
          ? 'bg-red-500 text-white hover:bg-red-600'
          : 'bg-white text-gray-700 hover:bg-gray-100 border border-gray-300'
      } ${saving ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'} ${className}`}
      aria-label={saved ? 'Unsave heart' : 'Save heart'}
    >
      {saved ? <HeartIconSolid className="w-5 h-5" /> : <HeartIcon className="w-5 h-5" />}
      <span className="hidden lg:block">{saved ? 'Saved' : 'Save'}</span>
    </button>
  );
}
